import React from 'react'
import { CheckSquare, Lock, Trophy, ArrowRight } from 'lucide-react'

const HowItWorks = ({ trackEvent }) => {
  const steps = [
    {
      number: 1,
      title: 'Choose Your Battle',
      description:
        'Select which apps to lock (Instagram, TikTok, Facebook, etc.) and pick a devotion from our library. Or go silent mode for unguided prayer.',
      time: '15 seconds',
      icon: CheckSquare,
      badgeBg: 'bg-prayer-blue',
      iconText: 'text-prayer-blue',
      pingBg: 'bg-prayer-blue',
    },
    {
      number: 2,
      title: 'Lock In & Listen',
      description:
        'Tap "Begin Prayer." Your selected apps instantly lock. The devotion plays with gentle guidance. Your countdown timer shows freedom approaching. Can\'t cheat — even force-closing won\'t unlock apps early.',
      time: '5-60 minutes (you choose)',
      icon: Lock,
      badgeBg: 'bg-streak-gold',
      iconText: 'text-streak-gold',
      pingBg: 'bg-streak-gold',
    },
    {
      number: 3,
      title: 'Streak Up & Share',
      description:
        'Session ends. Your streak increments. Hit 7 days? Unlock the "Faithful Week" badge. Your Pod sees your progress. They send encouragement. You check the leaderboard. Repeat tomorrow.',
      time: 'Ongoing',
      icon: Trophy,
      badgeBg: 'bg-pod-green',
      iconText: 'text-pod-green',
      pingBg: 'bg-pod-green',
    },
  ]

  return (
    <section id="how-it-works" className="section-padding bg-white">
      <div className="container-custom">
        {/* Headline */}
        <div className="text-center mb-16 animate-on-scroll">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-heading-dark mb-4">
            From Scrolling to Praying in <span className="text-gradient">3 Taps</span>
          </h2>
          <p className="text-lg md:text-xl text-body-gray max-w-3xl mx-auto">
            No complicated setup. No willpower battles. Just a simple ritual
            that becomes your daily spiritual anchor.
          </p>
        </div>

        {/* Steps Timeline */}
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 md:gap-4 relative">
            {/* Connection Lines (Desktop Only) */}
            <div className="hidden md:block absolute top-16 left-0 right-0 h-1 bg-grace-gray -z-10">
              <div className="h-full bg-gradient-to-r from-prayer-blue via-streak-gold to-pod-green w-2/3"></div>
            </div>

            {steps.map((step, index) => {
              const Icon = step.icon
              return (
                <div
                  key={index}
                  className="relative animate-on-scroll"
                  style={{ animationDelay: `${index * 0.15}s` }}
                >
                  {/* Step Number Badge */}
                  <div className="flex justify-center mb-6">
                    <div className={`w-20 h-20 rounded-full ${step.badgeBg} flex items-center justify-center text-white text-3xl font-bold shadow-lg relative`}>
                      {step.number}
                      <div className={`absolute inset-0 rounded-full ${step.pingBg} animate-ping opacity-20`}></div>
                    </div>
                  </div>

                  {/* Content Card */}
                  <div className="card h-full">
                    <div className="text-center mb-4">
                      <Icon size={32} className={`${step.iconText} mx-auto mb-3`} />
                      <h3 className="text-xl font-bold text-heading-dark mb-2">{step.title}</h3>
                      <div className="text-xs font-semibold text-prayer-blue bg-prayer-blue/10 rounded-full py-1 px-3 inline-block mb-4">
                        {step.time}
                      </div>
                    </div>
                    <p className="text-body-gray leading-relaxed">{step.description}</p>
                  </div>

                  {/* Arrow (Desktop Only) */}
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-16 -right-4 text-prayer-blue">
                      <ArrowRight size={32} />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16 animate-on-scroll">
          <a
            href="#download"
            onClick={() => trackEvent('how_it_works_cta_click')}
            className="btn-primary text-lg inline-block"
          >
            Try Your First Session Free
          </a>
        </div>
      </div>
    </section>
  )
}

export default HowItWorks
